import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import { mergeDateTimeString, toDateString } from "helpers";
import { createCompleteVisitVars } from "operations/mutations/CompleteVisit";
import {
  MeterReadingInputType,
  PropertyValueInputType,
  StockStore,
} from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { State } from "./visit.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

// Definition of actual actions
export const asyncMutations = {
  completeVisit: createAppAsyncThunk(
    "visit/completeVisit",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const {
        visit: { jobVisits },
        user: { userVar },
      } = getState();
      if (!userVar) return rejectWithValue("No user");
      const visit = jobVisits[jobId];
      const completeVisitVars = createCompleteVisitVars(jobId, userVar.suppliedStoreId, visit);

      const { data, errors, queued } = await graphqlRequest(sdk.completeVisitWithParts, {
        thunkName: "visit/completeVisit",
        thunkProps: props,
        variables: completeVisitVars,
        onError: [{ type: "jobs/resetJobStatus", payload: { jobId } }],
      });

      if (errors) return rejectWithValue(errors);
      if (data && !data.completeVisitWithParts) return rejectWithValue("something went wrong");
      return { queued, followUp: visit.followUp.followUpChecked };
    }
  ),
  startVisitTravel: createAppAsyncThunk(
    "visit/startVisitTravel",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { jobVisits } = getState().visit;
      let firstTravelTime = jobVisits[jobId].travelTimes[0];
      if (!firstTravelTime) return rejectWithValue("No times");
      let startTime = mergeDateTimeString(firstTravelTime.startDate, firstTravelTime.startTime);

      const { data, errors, queued } = await graphqlRequest(sdk.startVisitTravel, {
        thunkName: "visit/startVisitTravel",
        thunkProps: props,
        variables: { jobId, startTime },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.startVisitTravel) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  startVisitWork: createAppAsyncThunk(
    "visit/startVisitWork",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { jobVisits } = getState().visit;
      const { travelTimes } = jobVisits[jobId];
      const lastTravelTime = [...travelTimes].pop();
      const now = toDateString(Date.now());

      const { data, errors, queued } = await graphqlRequest(sdk.startVisitWork, {
        thunkName: "visit/startVisitWork",
        thunkProps: props,
        variables: {
          jobId,
          startTime: now,
          travelStopTime: lastTravelTime?.stopTime || now,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.startVisitWork) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  updateETA: createAppAsyncThunk(
    "visit/updateETA",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { jobVisits } = getState().visit;
      const eta = jobVisits[jobId].travelEta;
      if (!eta) return rejectWithValue("No ETA");
      const { data, errors, queued } = await graphqlRequest(sdk.updateETA, {
        thunkName: "visit/updateETA",
        thunkProps: props,
        variables: {
          jobId,
          eta,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateETA) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  updateVisitInformation: createAppAsyncThunk(
    "visit/updateVisitInformation",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { jobVisits } = getState().visit;
      const { travelMileage } = jobVisits[jobId];
      const { data, errors, queued } = await graphqlRequest(sdk.updateVisitInformation, {
        thunkName: "visit/updateVisitInformation",
        thunkProps: props,
        variables: {
          jobId,
          miles: travelMileage,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateVisitInformation) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  updateVisitTravel: createAppAsyncThunk(
    "visit/updateVisitTravel",
    async (props: { jobId: string }, { getState, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { jobVisits } = getState().visit;
      const { travelTimes, travelMileage, errors: vErrors } = jobVisits[jobId];
      let errorKeys = Object.keys(vErrors);
      if (errorKeys.includes("travelTimes") || errorKeys.includes("travelMileage")) {
        return rejectWithValue("Invalid travelTimes or mileage");
      }
      const { data, errors, queued } = await graphqlRequest(sdk.updateVisitTravel, {
        thunkName: "visit/updateVisitTravel",
        thunkProps: props,
        variables: {
          jobId,
          travelTimes: travelTimes.map((tt) => ({
            startTime: mergeDateTimeString(tt.startDate, tt.startTime),
            stopTime:
              tt.stopDate && tt.stopTime ? mergeDateTimeString(tt.stopDate, tt.stopTime) : null,
          })),
          miles: travelMileage,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateVisitTravel) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  updateMachineCustomProps: createAppAsyncThunk(
    "visit/updateMachineCustomProps",
    async (
      props: { jobId: string; properties: PropertyValueInputType[] },
      { rejectWithValue, extra: { sdk } }
    ) => {
      const { jobId, properties } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateMachineCustomProperties, {
        thunkName: "visit/updateMachineCustomProps",
        thunkProps: props,
        variables: {
          jobId,
          properties,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMachineCustomProperties)
        return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  //AddChecklistDialog
  addChecklist: createAppAsyncThunk(
    "visit/addChecklist",
    async (
      props: { jobId: string; checklistCode: string },
      { rejectWithValue, extra: { sdk } }
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.addChecklist, {
        thunkName: "visit/addChecklist",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.addChecklist) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
  updateMeters: createAppAsyncThunk(
    "visit/updateMeters",
    async (
      props: { jobId: string; meters: MeterReadingInputType | MeterReadingInputType[] },
      { rejectWithValue, extra: { sdk } }
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateMeters, {
        thunkName: "visit/updateMeters",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMeters) return rejectWithValue("something went wrong");
      return { data, queued };
    }
  ),
};

export const mutationBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(asyncMutations.completeVisit.rejected, (state, { payload: errors, meta }) => {
    if (!errors) throw new Error("Something went horribly wrong; error undefined");
    if (typeof errors === "string") throw new Error(errors);

    const id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;

    const { infoString } = ((messages: string[]): { shortMessage: string; infoString?: string } => {
      for (let message of messages) {
        if (!message.includes("|")) continue;
        const [s0] = message.split(" at ");
        const [shortMessage, infoString] = s0.split("|");
        return { shortMessage, infoString };
      }
      return {
        shortMessage: messages[0],
      };
    })(errors.map((e) => e.message));

    if (infoString) {
      const infos = infoString.split(",");
      for (let info of infos) {
        if (info.includes("CompleteVisit")) {
          state.jobVisits[id].newWorkUnitId = parseInt(info.split(":")[1]);
        }

        if (info.includes("UploadFiles")) {
          state.jobVisits[id].files = [];
        }

        if (info.includes("AddParts")) {
          const addedParts = info.split(":")[1]?.split(";");
          const { usedParts } = state.jobVisits[id];
          usedParts.map((p) =>
            addedParts.includes(p.part.id!) && p.part.stockStore !== StockStore.Other
              ? (p.uploaded = true)
              : p
          );
        }

        if (info.includes("RequestPart")) {
          const { usedParts } = state.jobVisits[id];
          usedParts.map((p) => (p.part.stockStore === StockStore.Other ? (p.uploaded = true) : p));
        }

        if (info.includes("FillChecklist")) {
          const { checklists } = state.jobVisits[id];
          checklists.map((cl) => (cl.uploaded = true));
        }
      }
    }
  });
  builder.addCase(asyncMutations.completeVisit.pending, (state, { meta }) => {
    if (meta.queued) return state;
    let id = state.selectedJobId;
    if (!id) throw Error("Missing selectedId");
    state.jobVisits[id].submitLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.completeVisit.fulfilled, (state, { meta }) => {
    let id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    return state;
  });
  builder.addCase(asyncMutations.startVisitTravel.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    return state;
  });
  builder.addCase(asyncMutations.startVisitTravel.fulfilled, (state) => {
    return state;
  });
  builder.addCase(asyncMutations.startVisitWork.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    return state;
  });
  builder.addCase(
    asyncMutations.startVisitWork.fulfilled,
    (state, { payload: { data, queued }, meta: { arg } }) => {
      let dateTime: string | null;

      if (!queued) dateTime = toDateString(data?.startVisitWork?.workTimes[0].startTime);
      else dateTime = toDateString(Date.now());

      if (!dateTime) return state;
      state.jobVisits[arg.jobId].workTimes = [
        {
          startDate: dateTime,
          startTime: dateTime,
        },
      ];
      return state;
    }
  );
  builder.addCase(asyncMutations.updateETA.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    return state;
  });
  builder.addCase(asyncMutations.updateETA.fulfilled, (state) => {
    return state;
  });
  builder.addCase(
    asyncMutations.updateVisitInformation.rejected,
    (state, { payload: errors, meta }) => {
      if (meta.aborted) return state;
      return state;
    }
  );
  builder.addCase(asyncMutations.updateVisitInformation.fulfilled, (state) => {
    return state;
  });
  builder.addCase(asyncMutations.updateVisitTravel.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    return state;
  });
  builder.addCase(asyncMutations.updateVisitTravel.fulfilled, (state) => {
    return state;
  });
  builder.addCase(asyncMutations.updateMachineCustomProps.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.machineProperties.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineCustomProps.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.machineProperties.loading = false;
  });
  builder.addCase(asyncMutations.updateMachineCustomProps.fulfilled, (state) => {
    state.machineProperties.loading = false;
    return state;
  });
  // AddChecklistDialog
  builder.addCase(asyncMutations.addChecklist.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.addChecklist.addLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.addChecklist.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.addChecklist.addLoading = false;
  });
  builder.addCase(
    asyncMutations.addChecklist.fulfilled,
    (state, { payload: { data }, meta: { arg } }) => {
      const newChecklist = data?.addChecklist;
      if (newChecklist) {
        const currentChecklists = state.jobVisits[arg.jobId].checklists;
        state.jobVisits[arg.jobId].checklists = [
          ...currentChecklists,
          {
            checklist: newChecklist,
            uploaded: false,
            hasPreStart: newChecklist.questions.some((q) => q.isRequiredPreStart),
          },
        ];
      }
      state.addChecklist.addLoading = false;
      return state;
    }
  );
};
